import { Component, EventEmitter, Output, Inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { forkJoin, of } from 'rxjs';
import { catchError, map, finalize } from 'rxjs/operators';
import { FacilitiesService, Facility } from 'src/app/handweave-api';
import { ApiConfig } from '../../api-config/api-config';

export interface AddFacilitiesCsvDialogResult {
  facilities: Facility[];
}

@Component({
  selector: 'app-add-facilities-csv-dialog',
  templateUrl: './add-facilities-csv-dialog.component.html',
  styleUrl: './add-facilities-csv-dialog.component.scss',
  standalone: false
})
export class AddFacilitiesCsvDialogComponent {
  @Output() facilitiesAdded = new EventEmitter<Facility[]>();

  csvInput = new UntypedFormControl('');
  isLoading = false;
  foundFacilities: Facility[] = [];
  notFoundIds: string[] = [];
  hasResults = false;

  private readonly tenantId: string = ApiConfig.tenantId || "unknown";

  constructor(
    private readonly dialogRef: MatDialogRef<AddFacilitiesCsvDialogComponent>,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly facilitiesApi: FacilitiesService
  ) { }

  onAddClick() {
    const csvValue = this.csvInput.value?.trim();
    if (!csvValue) {
      return;
    }

    this.isLoading = true;
    this.foundFacilities = [];
    this.notFoundIds = [];
    this.hasResults = false;

    // Parse CSV and clean up IDs
    let facilityIds = csvValue
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    if (facilityIds.length === 0) {
      this.isLoading = false;
      return;
    }

    // dedupe IDs by converting to a set
    facilityIds = [...new Set(facilityIds)];

    // Create observables for each facility lookup
    const facilityLookups = facilityIds.map(id => 
      this.facilitiesApi.facilitiesGetById({
        tenantId: this.tenantId,
        id: id
      }).pipe(
        catchError(() => of(null)),
        map(facility => ({ id, facility }))
      )
    );

    // Execute all lookups in parallel
    forkJoin(facilityLookups).pipe(
      finalize(() => {
        this.isLoading = false;
        this.hasResults = true;
      })
    ).subscribe((results: Array<{id: string, facility: Facility | null}>) => {
      for (const result of results) {
        if (result.facility) {
          this.foundFacilities.push(result.facility);
        } else {
          this.notFoundIds.push(result.id);
        }
      }
    });
  }

  onConfirmClick() {
    const result: AddFacilitiesCsvDialogResult = {
      facilities: this.foundFacilities
    };
    this.dialogRef.close(result);
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onClearClick() {
    this.csvInput.setValue('');
    this.foundFacilities = [];
    this.notFoundIds = [];
    this.hasResults = false;
  }
}
